"use client"

import { useEffect, useState, useMemo } from "react"
import { usePrivy, useSendTransaction } from "@privy-io/react-auth"
import { useChainId, useWalletClient } from "wagmi"
import { createPublicClient, http, type Address, type Chain } from "viem"
import { createSmartAccountClient } from "permissionless"
import { toSafeSmartAccount } from "permissionless/accounts"
import { createPimlicoClient } from "permissionless/clients/pimlico"
import { entryPoint07Address } from "viem/account-abstraction"
import {
  isGasSponsorshipSupported,
  getChainGasSponsorshipConfig,
  getApiKey,
} from "./config/gasSponsorship"
import { optimismSepolia, optimism, arbitrum, berachain } from "viem/chains"

// Map of chain IDs to chain objects
const CHAIN_MAP: Record<number, Chain> = {
  [optimismSepolia.id]: optimismSepolia,
  [optimism.id]: optimism,
  [arbitrum.id]: arbitrum,
  [berachain.id]: berachain,
}

/**
 * Dynamic Gas Sponsorship Hook
 *
 * Implements gas sponsorship for any chain configured in gasSponsorship.ts using:
 * - Privy Smart Wallet as the account foundation
 * - Configurable providers (Pimlico, Alchemy, Biconomy, etc.)
 * - Chain-specific settings from centralized configuration
 *
 * This provides seamless gas sponsorship experience for any supported chain
 * based on the configuration in gasSponsorship.ts
 */
export const useDynamicGasSponsorship = () => {
  const [smartAccountClient, setSmartAccountClient] = useState<any>(null)
  const [isInitializing, setIsInitializing] = useState(false)
  const [isReady, setIsReady] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { user, authenticated } = usePrivy()
  const chainId = useChainId()
  const { sendTransaction } = useSendTransaction()
  const { data: walletClient } = useWalletClient()

  // Get configuration for current chain
  const chainConfig = getChainGasSponsorshipConfig(chainId)
  const isSupported = isGasSponsorshipSupported(chainId)
  const currentChain = chainId ? CHAIN_MAP[chainId] : null
  const apiKey = chainId ? getApiKey(chainId) : null
  const smartWalletAddress = user?.smartWallet?.address as Address | undefined

  // Dynamic URLs based on configuration
  const bundlerUrl = useMemo(() => {
    if (!chainConfig?.config.bundlerUrl || !apiKey) {
      return null
    }
    return `${chainConfig.config.bundlerUrl}?apikey=${apiKey}`
  }, [chainConfig?.config.bundlerUrl, apiKey])

  const paymasterUrl = useMemo(() => {
    if (!chainConfig?.config.paymasterUrl || !apiKey) {
      return null
    }
    return `${chainConfig.config.paymasterUrl}?apikey=${apiKey}`
  }, [chainConfig?.config.paymasterUrl, apiKey])

  const publicClient = useMemo(() => {
    if (!isSupported || !currentChain) {
      return null
    }

    return createPublicClient({
      transport: http(currentChain.rpcUrls.default.http[0]),
      chain: currentChain,
    })
  }, [isSupported, currentChain])

  /**
   * Initialize the gas sponsorship system for the current chain
   */
  const initializeGasSponsorship = async () => {
    if (
      !isSupported ||
      !chainConfig ||
      !currentChain ||
      !authenticated ||
      !smartWalletAddress ||
      !walletClient ||
      !apiKey ||
      !publicClient ||
      !bundlerUrl ||
      !paymasterUrl
    ) {
      return
    }

    setIsInitializing(true)
    setError(null)

    try {
      // Only initialize if the chain uses azuro-style-pimlico
      // Other provider styles can be added here later
      if (chainConfig.style !== "azuro-style-pimlico") {
        throw new Error(
          `Provider style ${chainConfig.style} not yet implemented`
        )
      }

      // Create Safe smart account using the wallet client as owner
      const safeAccount = await toSafeSmartAccount({
        client: publicClient,
        owners: [walletClient], // Use wallet client as owner
        version: "1.4.1",
        entryPoint: {
          address: entryPoint07Address,
          version: "0.7",
        },
      })

      // Create Pimlico paymaster client
      const paymasterClient = createPimlicoClient({
        transport: http(paymasterUrl),
        entryPoint: {
          address: entryPoint07Address,
          version: "0.7",
        },
      })

      // Test paymaster connectivity
      const gasPrice = await paymasterClient.getUserOperationGasPrice()

      // Create smart account client with bundler and paymaster
      const smartClient = createSmartAccountClient({
        account: safeAccount,
        chain: currentChain,
        bundlerTransport: http(bundlerUrl),
        paymaster: paymasterClient,
        userOperation: {
          estimateFeesPerGas: async () => {
            return (await paymasterClient.getUserOperationGasPrice()).fast
          },
        },
      })

      setSmartAccountClient(smartClient)
      setIsReady(true)
    } catch (error) {
      setError(error instanceof Error ? error.message : "Unknown error")
      setIsReady(false)
    } finally {
      setIsInitializing(false)
    }
  }

  useEffect(() => {
    if (
      isSupported &&
      chainConfig &&
      authenticated &&
      smartWalletAddress &&
      walletClient &&
      apiKey
    ) {
      initializeGasSponsorship()
    } else {
      setSmartAccountClient(null)
      setIsReady(false)
      setError(null)
    }
  }, [
    isSupported,
    chainConfig,
    authenticated,
    smartWalletAddress,
    walletClient,
    apiKey,
    chainId,
  ])

  /**
   * Execute a gasless transaction on the current chain
   */
  const executeSponsoredTransaction = async (
    to: Address,
    data: `0x${string}`,
    value: bigint = BigInt(0)
  ): Promise<`0x${string}`> => {
    if (!smartAccountClient || !isReady) {
      throw new Error(`Gas sponsorship not ready for chain ${chainId}`)
    }

    try {
      // Send user operation through bundler with paymaster sponsorship
      const userOpHash = await smartAccountClient.sendTransaction({
        to,
        data,
        value,
      })

      return userOpHash
    } catch (error) {
      throw error
    }
  }

  /**
   * Get gas sponsorship info for current chain
   */
  const getGasSponsorshipInfo = async () => {
    if (!smartAccountClient || !isReady || !chainConfig) {
      throw new Error("Gas sponsorship not ready")
    }

    try {
      // Create a new paymaster client to get gas price info
      const paymasterClient = createPimlicoClient({
        transport: http(paymasterUrl!),
        entryPoint: {
          address: entryPoint07Address,
          version: "0.7",
        },
      })

      // Get current gas prices from paymaster
      const gasPrice = await paymasterClient.getUserOperationGasPrice()

      return {
        sponsored: true,
        provider: `${chainConfig.provider} (${chainConfig.chainName})`,
        chainId: chainConfig.chainId,
        chainName: chainConfig.chainName,
        maxFeePerGas: gasPrice.fast.maxFeePerGas,
        maxPriorityFeePerGas: gasPrice.fast.maxPriorityFeePerGas,
        gasLimit: "Estimated automatically",
        estimatedSavings: "Gas fees sponsored through Smart Wallet",
      }
    } catch (error) {
      throw error
    }
  }

  /**
   * Fallback transaction using Privy's sendTransaction
   */
  const executeFallbackTransaction = async (
    to: Address,
    data: `0x${string}`,
    value: bigint = BigInt(0)
  ): Promise<`0x${string}`> => {
    const txResponse = await sendTransaction({
      to,
      data,
      value: value.toString(),
    })

    return txResponse.hash as `0x${string}`
  }

  return {
    // Status
    isReady,
    isInitializing,
    error,
    isSupported,
    smartWalletAddress,
    chainId,
    chainName: chainConfig?.chainName || `Chain ${chainId}`,

    // Gas sponsorship capabilities
    hasGasSponsorship: isReady && isSupported,
    canSponsorGas: isReady && isSupported,

    // Transaction execution
    executeGaslessTransaction: executeSponsoredTransaction,
    getGasSponsorshipInfo,
    executeFallbackTransaction,

    // Configuration info
    provider: chainConfig?.provider,
    style: chainConfig?.style,
    features: chainConfig?.features,

    // Debug info
    smartAccountClient,
    debugInfo: {
      bundlerUrl,
      paymasterUrl,
      apiKey: !!apiKey,
      chainConfig,
      isSupported,
    },
  }
}
